import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Eye,
    EyeOff,
    Lock,
    CheckCircle,
    ShieldCheck,
    ChevronLeft,
    KeyRound
} from "lucide-react";
import { useAdminAuth } from "../services/admin/AdminAuthServices";
import { useNotification } from "../context/NotificationContext";
import { motion, AnimatePresence } from "framer-motion";

export default function ChangePassword() {
    const navigate = useNavigate();
    const { changePasswordAPI, loading: isSubmitting } = useAdminAuth();
    const { showNotification } = useNotification();

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [, setError] = useState("");

    // Password strength calculations
    const hasMinLength = newPassword.length >= 8;
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);
    const allRequirementsMet = hasMinLength && hasUpperCase && hasDigit;
    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;


    // Calculate password strength
    const getPasswordStrength = () => {
        if (!newPassword) return { label: "", color: "", width: "0%" };
        let strength = 0;
        if (hasMinLength) strength++;
        if (hasUpperCase) strength++;
        if (hasDigit) strength++;
        if (newPassword.length >= 12) strength++;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) strength++;

        if (strength <= 2)
            return { label: "Weak", color: "bg-red-500", width: "33%" };
        if (strength <= 3)
            return { label: "Medium", color: "bg-yellow-500", width: "66%" };
        return { label: "Strong", color: "bg-green-500", width: "100%" };
    };

    const strength = getPasswordStrength();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!allRequirementsMet) {
            showNotification({
                type: "failed",
                title: "Weak Password",
                message: "Please meet all password requirements."
            });
            return;
        }

        if (!passwordsMatch) {
            showNotification({
                type: "failed",
                title: "Mismatch",
                message: "New passwords do not match."
            });
            return;
        }

        if (currentPassword === newPassword) {
            showNotification({
                type: "failed",
                title: "Identical Password",
                message: "New password cannot be the same as current password."
            });
            return;
        }

        setError(""); // Clear any previous errors if any persist

        try {
            const res = await changePasswordAPI({
                current_password: currentPassword.trim(),
                new_password: newPassword.trim()
            });

            if (res && res.status === "success") {
                setTimeout(() => {
                    navigate("/admin/dashboard");
                }, 1500);
            }
        } catch (err) {
            console.error("Change password error:", err);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 lg:p-10 max-w-2xl mx-auto"
        >
            {/* Back Button */}
            <button
                onClick={() => navigate("/admin/dashboard")}
                className="flex items-center gap-2 text-black dark:text-white/60 hover:text-black/70 dark:hover:text-white transition-colors text-xs font-bold tracking-widest uppercase mb-8"
            >
                <ChevronLeft size={16} /> BACK TO DASHBOARD
            </button>

            {/* Header Section */}
            <div className="mb-10 text-center sm:text-left">
                <h1
                    className="text-3xl lg:text-5xl font-normal text-black dark:text-white mb-3"
                    style={{ fontFamily: "Playfair Display, serif" }}
                >
                    Security & Privacy
                </h1>
                <p className="text-black dark:text-white/50 text-sm md:text-base italic font-serif">
                    Create a strong, unique password to secure your administrator account.
                </p>
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-xl shadow-black/5 overflow-hidden">
                {/* Visual Tab / Indicator */}
                <div className="h-1.5 w-full bg-jazz-brown" />

                <form onSubmit={handleSubmit} className="p-8 lg:p-10 space-y-8">

                    {/* Current Password Field */}
                    <div className="space-y-3">
                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-[0.2em]">
                            Current Password
                        </label>
                        <div className="relative group">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-black group-focus-within:text-jazz-brown transition-colors">
                                <ShieldCheck size={20} />
                            </div>
                            <input
                                type={showCurrentPassword ? "text" : "password"}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="w-full pl-12 pr-12 py-4 bg-[#FAFAFA] dark:bg-[#0A0A0A] border border-black/5 dark:border-white/5 rounded-xl text-black dark:text-white placeholder-black/60 focus:outline-none focus:ring-2 focus:ring-jazz-brown/30 transition-all font-medium"
                                placeholder="••••••••••••"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-black hover:text-jazz-brown transition-colors"
                            >
                                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="h-px bg-black/5" />

                    {/* New Password Field */}
                    <div className="space-y-3">
                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-[0.2em]">
                            New Password
                        </label>
                        <div className="relative group">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-black group-focus-within:text-jazz-brown transition-colors">
                                <KeyRound size={20} />
                            </div>
                            <input
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full pl-12 pr-12 py-4 bg-[#FAFAFA] dark:bg-[#0A0A0A] border border-black/5 dark:border-white/5 rounded-xl text-black dark:text-white placeholder-black/60 focus:outline-none focus:ring-2 focus:ring-jazz-brown/30 transition-all font-medium font-mono tracking-wider"
                                placeholder="Enter new password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-black hover:text-jazz-brown transition-colors"
                            >
                                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        {/* Password Strength Meter */}
                        <AnimatePresence>
                            {newPassword && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="space-y-4 pt-2"
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">Strength: <span className={strength.color.replace('bg-', 'text-')}>{strength.label}</span></span>
                                    </div>
                                    <div className="w-full h-1 bg-black/5 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: strength.width }}
                                            className={`h-full ${strength.color} transition-all duration-500`}
                                        />
                                    </div>

                                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                                        {[
                                            { label: "8+ characters", met: hasMinLength },
                                            { label: "Uppercase", met: hasUpperCase },
                                            { label: "Number", met: hasDigit }
                                        ].map((req, i) => (
                                            <div key={i} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.1em] ${req.met ? "text-green-500" : "text-black/60 dark:text-white/40"}`}>
                                                {req.met ? <CheckCircle size={12} /> : <div className="w-3 h-3 rounded-full border border-current" />}
                                                {req.label}
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Confirm Password Field */}
                    <div className="space-y-3">
                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-[0.2em]">
                            Confirm New Password
                        </label>
                        <div className="relative group">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-black group-focus-within:text-jazz-brown transition-colors">
                                <Lock size={20} />
                            </div>
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full pl-12 pr-12 py-4 bg-[#FAFAFA] dark:bg-[#0A0A0A] border border-black/5 dark:border-white/5 rounded-xl text-black dark:text-white placeholder-black/60 focus:outline-none focus:ring-2 focus:ring-jazz-brown/30 transition-all font-medium font-mono"
                                placeholder="Re-enter new password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-black hover:text-jazz-brown transition-colors"
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {confirmPassword && (
                            <p className={`text-xs font-bold uppercase tracking-widest ${passwordsMatch ? "text-green-500" : "text-red-500"}`}>
                                {passwordsMatch ? "✓ Passwords Match" : "✕ Passwords do not match"}
                            </p>
                        )}
                    </div>

                    {/* Submit Actions */}
                    <div className="pt-6">
                        <button
                            type="submit"
                            disabled={isSubmitting || !allRequirementsMet || !passwordsMatch || !currentPassword}
                            className="w-full relative flex items-center justify-center gap-3 bg-[#111111] text-white py-5 rounded-2xl text-xs font-bold tracking-[0.3em] uppercase hover:bg-jazz-brown transition-all shadow-xl shadow-black/10 disabled:opacity-30 disabled:cursor-not-allowed group overflow-hidden"
                        >
                            <span className="relative z-10">{isSubmitting ? "PROCESSING..." : "UPDATE CREDENTIALS"}</span>
                            {!isSubmitting && <KeyRound size={16} className="relative z-10 group-hover:rotate-12 transition-transform" />}
                            <div className="absolute inset-0 bg-jazz-brown opacity-0 group-hover:opacity-10 transition-opacity" />
                        </button>

                        <p className="text-center text-xs text-black dark:text-white/40 font-bold uppercase tracking-widest mt-6">
                            Secured by Temidayo Balogun session management
                        </p>
                    </div>
                </form>
            </div>
        </motion.div>
    );
}
